import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GalleryGrid } from '@/components/gallery/GalleryGrid';
import { ProjectFilter, type FilterValue } from '@/components/gallery/ProjectFilter';
import { SEOHead } from '@/components/layout/SEOHead';
import { FadeIn } from '@/components/motion/FadeIn';
import { getAllProjects } from '@/lib/content';
import type { ProjectTypology } from '@/types/content';

const VALID_TYPES: ProjectTypology[] = ['residential', 'commercial', 'hospitality', 'multifamily', 'retail'];

function parseType(raw: string | null): FilterValue {
  if (!raw) return 'all';
  if (raw === 'all') return 'all';
  if ((VALID_TYPES as string[]).includes(raw)) return raw as ProjectTypology;
  return 'all';
}

export function ProjectsPage() {
  const projects = getAllProjects();
  const [searchParams, setSearchParams] = useSearchParams();
  const type = parseType(searchParams.get('type'));

  const filtered = useMemo(
    () => (type === 'all' ? projects : projects.filter((p) => p.typology === type)),
    [projects, type]
  );

  const onChange = (next: FilterValue) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') {
      params.delete('type');
    } else {
      params.set('type', next);
    }
    setSearchParams(params, { replace: true });
  };

  return (
    <>
      <SEOHead
        title="Projects · Brian Ellison Design"
        description="Selected projects by Brian Ellison Design — residential, commercial, and hospitality interiors across Florida."
        canonical="https://brianellisondesign.com/projects"
        ogImage="/posters/og-projects.jpg"
      />
      <section className="page-hero">
        <div className="container">
          <FadeIn>
            <p className="eyebrow">Portfolio</p>
            <h1 className="page-hero__title">Selected work.</h1>
            <p className="subtitle">
              A small, considered collection. Each project is shaped by its place, its
              materials, and the people who will live with it.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="container">
        <FadeIn>
          <ProjectFilter projects={projects} value={type} onChange={onChange} />
        </FadeIn>
      </section>

      <section className="container section">
        {filtered.length > 0 ? (
          <GalleryGrid projects={filtered} />
        ) : (
          <p className="subtitle project-filter__empty">
            No projects in this category yet.
          </p>
        )}
      </section>
    </>
  );
}
